/**
 * Camada dos ITENS que andam pelo mapa. Fica POR CIMA das salas e é uma só
 * para a fábrica inteira, porque um item precisa poder ir de uma sala para
 * outra: cada um tem chave estável, então quando a planilha muda a etapa ou o
 * gestor muda o posto é o MESMO elemento que caminha do lugar antigo para o
 * novo (framer-motion anima x/y no mundo do mapa — funciona com qualquer zoom,
 * sem medir o DOM).
 *
 * Dois tipos de item, e a diferença entre eles tem de ser visível antes de
 * qualquer rótulo:
 *
 *   AMBIENTE — a linha da planilha. Ficha do tamanho do espaço, com glifo de
 *              status à esquerda e, quando o gestor desmembrou peças dela, o
 *              selo "N peças fora" à direita.
 *   PEÇA     — a peça desmembrada (`dados/pecas.js`). Pecinha MENOR, duas por
 *              espaço, com uma barra da cor do status na lateral: um derivado,
 *              não um ambiente.
 *
 * - Item que aparece: sai de onde estava antes (o "+N" em que morava, ou a
 *   entrada da fábrica, se acabou de entrar no fluxo).
 * - Item que some (carregado, concluído, ou foi para o "+N"): desbota.
 * - Primeira carga e modo Organizar: sem animação.
 * - Reduzir movimento (sistema ou "Animações da interface" desligadas): o
 *   MotionConfig do mapa troca o deslocamento por troca instantânea.
 */
import React, { memo, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

import { formatarDataLocal, plural } from "../../utils/dinheiro";
import { ROTULO_STATUS_AMBIENTE, ROTULO_STATUS_PECA } from "./dados/vocabulario";
import { chaveDaPeca, truncarTexto } from "./planta";
import { doSetor } from "./textos";

const EASE_MOVIMENTO = [0.77, 0, 0.175, 1];
const EASE_SAIDA = [0.23, 1, 0.32, 1];
const VELOCIDADE_MUNDO_POR_S = 1400;
const DURACAO_MIN_S = 0.35;
const DURACAO_MAX_S = 0.9;
const RAIO_ITEM = 5;
// Selo "N peças fora" na ponta direita da ficha do ambiente.
const SELO_PECAS = { largura: 16, altura: 14, margem: 3, fonte: 9.5, ampliacaoMax: 1.3 };
// Teto da fonte do "+N": fração da altura da ficha dele.
const FRACAO_FONTE_MAIS = 0.78;
// A barra da peça é PICOTADA (três segmentos) como a linha de destaque de um
// talão: é o que diz "fui destacada de um ambiente" em preto e branco, sem
// depender da cor do status nem do tamanho da ficha.
const BARRA_PECA = { segmentos: 3, fracaoVao: 0.14 };

const limitar = (v, min, max) => Math.min(max, Math.max(min, v));

/** Leitura do ambiente para quem usa leitor de tela. */
export function rotuloDoAmbiente(demanda) {
  const partes = [demanda.rotulo || "Ambiente sem identificação"];
  if (demanda.cliente) partes.push(demanda.cliente);
  partes.push(ROTULO_STATUS_AMBIENTE[demanda.statusApp] || "Aguardando");
  if (demanda.atrasada) {
    partes.push(demanda.prazoEfetivo ? `atrasado, prazo ${formatarDataLocal(demanda.prazoEfetivo)}` : "atrasado");
  }
  if (demanda.tarefas?.length) partes.push(plural(demanda.tarefas.length, "tarefa aberta", "tarefas abertas"));
  if (demanda.pecasFora) partes.push(`${plural(demanda.pecasFora, "peça", "peças")} fora`);
  return partes.join(", ");
}

/** Leitura da peça desmembrada: o que é, de qual ambiente saiu e como está. */
export function rotuloDaPeca(peca) {
  const partes = [`Peça ${peca.nome || peca.codigo || "sem nome"}`];
  if (peca.ambienteRotulo) partes.push(`de ${peca.ambienteRotulo}`);
  partes.push(ROTULO_STATUS_PECA[peca.status] || "Aguardando");
  if (peca.atrasada) {
    partes.push(peca.prazoEfetivo ? `atrasada, prazo ${formatarDataLocal(peca.prazoEfetivo)}` : "atrasada");
  }
  if (peca.tarefas?.length) partes.push(plural(peca.tarefas.length, "tarefa aberta", "tarefas abertas"));
  return partes.join(", ");
}

// Glifo de status à esquerda do ambiente: não depender só da cor.
function Glifo({ status, atrasada, x, y, k }) {
  if (atrasada) {
    return (
      <g className="glifo glifo-atraso" aria-hidden="true">
        <circle cx={x} cy={y} r={6 * k} />
        <text x={x} y={y} dy="0.35em" fontSize={9 * k} textAnchor="middle">!</text>
      </g>
    );
  }
  if (status === "em_producao") {
    return <path className="glifo" d={`M${x - 3 * k} ${y - 4.5 * k} L${x + 4.5 * k} ${y} L${x - 3 * k} ${y + 4.5 * k} Z`} aria-hidden="true" />;
  }
  if (status === "pausado" || status === "pausada") {
    return (
      <g className="glifo" aria-hidden="true">
        <rect x={x - 4 * k} y={y - 4.5 * k} width={2.8 * k} height={9 * k} rx={1 * k} />
        <rect x={x + 1.2 * k} y={y - 4.5 * k} width={2.8 * k} height={9 * k} rx={1 * k} />
      </g>
    );
  }
  return <circle className="glifo glifo-vazio" cx={x} cy={y} r={3.5 * k} aria-hidden="true" />;
}

function duracaoDoTrajeto(de, para) {
  if (!de) return DURACAO_MIN_S;
  const distancia = Math.hypot(para.x - de.x, para.y - de.y);
  return limitar(distancia / VELOCIDADE_MUNDO_POR_S, DURACAO_MIN_S, DURACAO_MAX_S);
}

/** Letras do nome que precisam sobrar para valer a pena mostrá-lo cortado. */
const MINIMO_DO_NOME = 2;

/**
 * Rótulo que cabe na ficha do ambiente: o rótulo inteiro, um pedaço do nome que
 * ainda diga alguma coisa, ou só o pedido. "1205 ·…" e "1205 · C…" não dizem
 * nada além do número e, enfileirados numa fila, viram sujeira visual — nesses
 * casos a ficha mostra só "1205", e o nome inteiro fica no toque e no zoom.
 */
export function rotuloQueCabe(demanda, largura, fonte) {
  const inteiro = truncarTexto(demanda.rotulo, largura, fonte);
  const pedido = String(demanda.pedido ?? "").trim();
  if (!pedido || !inteiro.endsWith("…")) return inteiro;
  const visivel = inteiro.slice(0, -1).trimEnd();
  if (!visivel.startsWith(pedido)) return inteiro;
  const doNome = visivel.slice(pedido.length).replace(/^\s*·\s*/, "");
  return doNome.length >= MINIMO_DO_NOME ? inteiro : truncarTexto(pedido, largura, fonte);
}

/**
 * De onde o item entra em cena: do lugar onde ele estava na leitura anterior
 * (o "+N" em que morava, por exemplo) ou da entrada da fábrica, quando é a
 * primeira vez que ele aparece no mapa.
 */
function useOrigem(chave, origensRef, entrada, primeiraCarga) {
  const [inicial] = useState(() => {
    if (primeiraCarga) return false;
    const antes = origensRef.current.get(chave);
    return antes ? { x: antes.x, y: antes.y, opacity: 0.4 } : { x: entrada.x, y: entrada.y, opacity: 0 };
  });
  return inicial;
}

function useTransicao(destino, inicial, organizando) {
  // Último destino JÁ desenhado (atualizado depois do commit, não no render,
  // para o StrictMode não zerar o trajeto): mede a distância da caminhada.
  const ultimoRef = useRef(inicial ? { x: inicial.x, y: inicial.y } : destino);
  const duracao = duracaoDoTrajeto(ultimoRef.current, destino);
  useEffect(() => {
    ultimoRef.current = { x: destino.x, y: destino.y };
  }, [destino.x, destino.y]);
  if (organizando) return { duration: 0 };
  return {
    x: { duration: duracao, ease: EASE_MOVIMENTO },
    y: { duration: duracao, ease: EASE_MOVIMENTO },
    opacity: { duration: 0.2 },
  };
}

function PulsoDoItem({ pulso, largura, altura, raio }) {
  if (!pulso) return null;
  return (
    <motion.rect
      key={pulso}
      className="pulso"
      width={largura}
      height={altura}
      rx={raio}
      initial={{ opacity: 0.8, scale: 1 }}
      animate={{ opacity: 0, scale: 1.18 }}
      transition={{ duration: 0.8, ease: EASE_SAIDA }}
      aria-hidden="true"
    />
  );
}

/**
 * Segmentos da barra picotada da peça. Devolve a lista de retângulos (y e
 * altura) que, com os vãos entre eles, ocupam a altura inteira da ficha.
 */
export function segmentosDaBarra(altura, segmentos = BARRA_PECA.segmentos) {
  const n = Math.max(1, segmentos);
  if (!(altura > 0)) return [];
  const vao = n > 1 ? (altura * BARRA_PECA.fracaoVao) / (n - 1) : 0;
  const lado = (altura - vao * (n - 1)) / n;
  return Array.from({ length: n }, (_, i) => ({ y: i * (lado + vao), altura: lado }));
}

/** Selo "N peças fora" na ponta da ficha: o ambiente perdeu pedaços de si. */
function SeloDePecas({ quantas, largura, altura, k, ampliacao }) {
  if (!quantas) return null;
  const l = SELO_PECAS.largura * k;
  const a = SELO_PECAS.altura * k;
  const x = largura - l - SELO_PECAS.margem * k;
  const y = (altura - a) / 2;
  const fonte = SELO_PECAS.fonte * k * Math.min(ampliacao || 1, SELO_PECAS.ampliacaoMax);
  return (
    <g className="selo-pecas" aria-hidden="true">
      <rect x={x} y={y} width={l} height={a} rx={3 * k} />
      {/* Dois riscos: o desenho de "saiu um pedaço daqui", legível mesmo sem
          o número (mapa muito afastado). */}
      <line x1={x + 2.5 * k} y1={y + a * 0.32} x2={x + 5 * k} y2={y + a * 0.32} />
      <line x1={x + 2.5 * k} y1={y + a * 0.68} x2={x + 5 * k} y2={y + a * 0.68} />
      {ampliacao > 0 && (
        <text x={x + l - 3 * k} y={y + a / 2} dy="0.36em" fontSize={fonte} textAnchor="end">{quantas}</text>
      )}
    </g>
  );
}

const Ambiente = memo(function Ambiente({ item, ampliacao, apagada, pulso, organizando, origensRef, entrada, primeiraCarga }) {
  const { demanda, largura, altura, k } = item;
  const destino = { x: item.x, y: item.y };
  const inicial = useOrigem(item.chaveLinha, origensRef, entrada, primeiraCarga);
  const transicao = useTransicao(destino, inicial, organizando);
  const comSelo = demanda.pecasFora > 0;
  const larguraDoRotulo = largura - 27 * k - (comSelo ? (SELO_PECAS.largura + SELO_PECAS.margem + 3) * k : 0);

  return (
    <motion.g
      className="ambiente"
      data-ambiente={item.chaveLinha}
      data-sala={item.setorId}
      data-status={demanda.statusApp}
      data-atrasada={demanda.atrasada ? "sim" : undefined}
      data-apagado={apagada ? "sim" : undefined}
      data-pulso={pulso ? "sim" : undefined}
      role="button"
      tabIndex={organizando ? -1 : 0}
      aria-label={rotuloDoAmbiente(demanda)}
      initial={inicial}
      animate={{ x: destino.x, y: destino.y, opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.25, ease: EASE_SAIDA } }}
      transition={transicao}
    >
      <PulsoDoItem pulso={pulso} largura={largura} altura={altura} raio={RAIO_ITEM * k} />
      {/* Sombra curta embaixo: a ficha POUSA na bancada. Sem ela, base e
          corpo alinhados, o ambiente vira etiqueta impressa no móvel. */}
      <rect className="item-sombra" y={1.6 * k} width={largura} height={altura} rx={RAIO_ITEM * k} aria-hidden="true" />
      <rect className="item-base" width={largura} height={altura} rx={RAIO_ITEM * k} />
      <rect className="item-corpo" width={largura} height={altura} rx={RAIO_ITEM * k} />
      <Glifo status={demanda.statusApp} atrasada={demanda.atrasada} x={11 * k} y={altura / 2} k={k} />
      {ampliacao > 0 && larguraDoRotulo > 0 && (
        <text className="item-rotulo" x={21 * k} y={altura / 2} dy="0.35em" fontSize={12 * k * ampliacao}>
          {rotuloQueCabe(demanda, larguraDoRotulo, 12 * k * ampliacao)}
        </text>
      )}
      <SeloDePecas quantas={demanda.pecasFora} largura={largura} altura={altura} k={k} ampliacao={ampliacao} />
      {/* Marca de "tem tarefa aberta" na quina. Encostada no canto (5k, o raio
          do arredondamento) virava borrão em cima da borda; afastada, lê-se
          como marca. Fica acima da linha do rótulo, que é centrada. */}
      {demanda.tarefas?.length > 0 && (
        // Com o selo de peças na ponta, a marca de tarefa recua a largura dele:
        // as duas encostadas viravam um borrão só.
        <circle
          className="item-tarefa"
          cx={largura - 6.5 * k - (comSelo ? (SELO_PECAS.largura + SELO_PECAS.margem) * k : 0)}
          cy={6.5 * k}
          r={2.8 * k}
          aria-hidden="true"
        />
      )}
    </motion.g>
  );
});

const Peca = memo(function Peca({ item, ampliacao, apagada, pulso, organizando, origensRef, entrada, primeiraCarga }) {
  const { peca, largura, altura, k } = item;
  const destino = { x: item.x, y: item.y };
  const inicial = useOrigem(chaveDaPeca(item.id), origensRef, entrada, primeiraCarga);
  const transicao = useTransicao(destino, inicial, organizando);
  const raio = RAIO_ITEM * 0.7 * k;
  const barra = 4 * k;
  const fonte = 10.5 * k * (ampliacao || 0);
  return (
    <motion.g
      className="peca"
      data-peca={item.id}
      data-sala={item.setorId}
      data-status={peca.status}
      data-atrasada={peca.atrasada ? "sim" : undefined}
      data-apagado={apagada ? "sim" : undefined}
      data-pulso={pulso ? "sim" : undefined}
      role="button"
      tabIndex={organizando ? -1 : 0}
      aria-label={rotuloDaPeca(peca)}
      initial={inicial}
      animate={{ x: destino.x, y: destino.y, opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.25, ease: EASE_SAIDA } }}
      transition={transicao}
    >
      <PulsoDoItem pulso={pulso} largura={largura} altura={altura} raio={raio} />
      <rect className="item-sombra" y={1.2 * k} width={largura} height={altura} rx={raio} aria-hidden="true" />
      <rect className="item-base" width={largura} height={altura} rx={raio} />
      <rect className="item-corpo" width={largura} height={altura} rx={raio} />
      {/* Barra picotada na lateral: é o que diz "pedaço destacado de um
          ambiente" de longe, quando nem o nome se lê — e diz pela forma, não
          pela cor (o ambiente não tem barra nenhuma). */}
      {segmentosDaBarra(altura).map((seg) => (
        <rect key={seg.y} className="peca-barra" y={seg.y} width={barra} height={seg.altura} rx={raio / 2} />
      ))}
      {ampliacao > 0 && (
        <text className="item-rotulo" x={barra + 4 * k} y={altura / 2} dy="0.35em" fontSize={fonte}>
          {truncarTexto(peca.nome || peca.codigo, largura - barra - 7 * k, fonte)}
        </text>
      )}
      {peca.tarefas?.length > 0 && (
        <circle className="item-tarefa" cx={largura - 4.5 * k} cy={4.5 * k} r={2.2 * k} aria-hidden="true" />
      )}
    </motion.g>
  );
});

function textoDoMais(mais, nomeDoSetor) {
  const total = mais.ocultas.length + (mais.ocultasPecas?.length || 0);
  const onde = mais.conteiner === "fila" ? `na Fila ${doSetor(nomeDoSetor)}` : `neste lugar ${doSetor(nomeDoSetor)}`;
  return `Mais ${plural(total, "item", "itens")} ${onde}`;
}

const Mais = memo(function Mais({ mais, nomeDoSetor, ampliacao, organizando }) {
  const { x, y, largura, altura, k } = mais;
  const atrasadas = [...mais.ocultas, ...(mais.ocultasPecas || [])].filter((i) => i.atrasada).length;
  const total = mais.ocultas.length + (mais.ocultasPecas?.length || 0);
  const texto = textoDoMais(mais, nomeDoSetor);
  // Numa sala apertada o "+N" é uma ficha baixa: o número nunca pode passar da
  // altura dela (com o zoom semântico a fonte cresce, a ficha não).
  const fonte = Math.min((ampliacao > 0 ? 12.5 * ampliacao : 16) * k, altura * FRACAO_FONTE_MAIS);
  return (
    <g
      className="mais"
      data-mais={mais.id}
      data-sala={mais.setorId}
      data-atrasada={atrasadas ? "sim" : undefined}
      role="button"
      tabIndex={organizando ? -1 : 0}
      aria-label={atrasadas ? `${texto}, ${atrasadas} atrasados` : texto}
    >
      <rect className="mais-corpo" x={x} y={y} width={largura} height={altura} rx={RAIO_ITEM * k} />
      <text
        className="mais-texto"
        x={x + largura / 2}
        y={y + altura / 2}
        dy="0.35em"
        fontSize={fonte}
        textAnchor="middle"
      >
        {`+${total}`}
      </text>
    </g>
  );
});

/**
 * `ambientes`/`pecas`/`mais` = montarPlanta; `ampliacaoPorSetor` =
 * {setorId: número}; `nomePorSetor` = {setorId: nome}; `passa`/`passaPeca` =
 * Set do filtro (null = sem filtro); `pulsos` = Map<"amb:<chave>"|"pec:<id>",
 * seq>; `origensRef` = ref com as posições da leitura anterior; `entrada` =
 * ponto de chegada no fluxo.
 */
function ItensSvg({
  ambientes,
  pecas,
  mais,
  ampliacaoPorSetor,
  nomePorSetor = {},
  passa,
  passaPeca,
  pulsos,
  organizando,
  origensRef,
  entrada,
  primeiraCarga,
}) {
  return (
    <g className="camada-itens">
      <AnimatePresence initial={false}>
        {ambientes.map((item) => (
          <Ambiente
            key={item.chaveLinha}
            item={item}
            ampliacao={ampliacaoPorSetor[item.setorId] || 0}
            apagada={Boolean(passa) && !passa.has(item.chaveLinha)}
            pulso={item.chave ? pulsos?.get(`amb:${item.chave}`) : undefined}
            organizando={organizando}
            origensRef={origensRef}
            entrada={entrada}
            primeiraCarga={primeiraCarga}
          />
        ))}
        {pecas.map((item) => (
          <Peca
            key={item.id}
            item={item}
            ampliacao={ampliacaoPorSetor[item.setorId] || 0}
            apagada={Boolean(passaPeca) && !passaPeca.has(item.id)}
            pulso={pulsos?.get(`pec:${item.id}`)}
            organizando={organizando}
            origensRef={origensRef}
            entrada={entrada}
            primeiraCarga={primeiraCarga}
          />
        ))}
      </AnimatePresence>
      {mais.map((m) => (
        <Mais
          key={m.id}
          mais={m}
          nomeDoSetor={nomePorSetor[m.setorId] || ""}
          ampliacao={ampliacaoPorSetor[m.setorId] || 0}
          organizando={organizando}
        />
      ))}
    </g>
  );
}

export default memo(ItensSvg);
